import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAlert } from '../components/GlobalAlert';

function EditProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();
  const { t } = useLanguage();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!profileData.username || !profileData.email) {
      showAlert(t('fillAllFields'), 'error');
      setLoading(false);
      return;
    }

    try {
      const result = await updateProfile(profileData);
      if (result.success) {
        showAlert('✅ ' + t('updateSuccess'), 'success');
        setProfileData({
          username: result.user?.username || profileData.username,
          email: result.user?.email || profileData.email
        });
      } else {
        if (result.message.includes('Token is not valid') || result.message.includes('No token')) {
          showAlert('🔐 ' + t('sessionExpired'), 'error');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          showAlert(result.message || t('updateError'), 'error');
        }
      }
    } catch (error) {
      showAlert(t('connectionError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showAlert(t('fillAllFields'), 'error');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showAlert(t('passwordMinLength'), 'error');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlert(t('passwordMismatch'), 'error');
      setPasswordLoading(false);
      return;
    }

    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        showAlert('🔐 ' + t('passwordChangeSuccess'), 'success');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        if (result.message.includes('Token is not valid') || result.message.includes('No token')) {
          showAlert('🔐 ' + t('sessionExpired'), 'error');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          showAlert(result.message || t('passwordChangeError'), 'error');
        }
      }
    } catch (error) {
      showAlert(t('connectionError'), 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '30px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      marginBottom: '20px',
    },
    title: {
      textAlign: 'center',
      marginBottom: '30px',
      color: '#333',
      fontSize: '24px',
      fontWeight: 'bold',
    },
    tabContainer: {
      display: 'flex',
      marginBottom: '30px',
      borderBottom: '2px solid #e5e7eb',
    },
    tab: {
      flex: 1,
      padding: '15px',
      textAlign: 'center',
      cursor: 'pointer',
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s ease',
      fontWeight: '500',
      fontSize: '16px',
    },
    activeTab: {
      borderBottomColor: '#3b82f6',
      color: '#3b82f6',
    },
    inactiveTab: {
      color: '#6b7280',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
    },
    input: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px',
      transition: 'border-color 0.3s ease',
    },
    button: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '12px 24px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>{t('editData')}</h2>
        
        <div style={styles.tabContainer}>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === 'profile' ? styles.activeTab : styles.inactiveTab)
            }}
            onClick={() => setActiveTab('profile')}
          >
            {t('profileData')}
          </div>
          <div
            style={{
              ...styles.tab,
              ...(activeTab === 'password' ? styles.activeTab : styles.inactiveTab)
            }}
            onClick={() => setActiveTab('password')}
          >
            {t('changePassword')}
          </div>
        </div>

        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="username" style={styles.label}>{t('username')}</label>
              <input
                type="text"
                id="username"
                name="username"
                value={profileData.username}
                onChange={handleProfileChange}
                placeholder={t('username')}
                required
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>{t('email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                placeholder={t('enterEmail')}
                style={styles.input}
                required
              />
            </div>
            
            <button 
              type="submit" 
              disabled={loading} 
              style={loading ? styles.buttonDisabled : styles.button}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              {loading ? t('updating') : t('saveData')}
            </button>
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="currentPassword" style={styles.label}>{t('currentPassword')}</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                placeholder={t('enterCurrentPassword')}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label htmlFor="newPassword" style={styles.label}>{t('newPassword')}</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder={t('enterNewPassword')}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>{t('confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                placeholder={t('confirmPassword')}
                required
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.outline = 'none';
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={passwordLoading} 
              style={passwordLoading ? styles.buttonDisabled : styles.button}
              onMouseEnter={(e) => {
                if (!passwordLoading) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!passwordLoading) {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              {passwordLoading ? t('changingPassword') : t('changePassword')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default EditProfilePage;